import { Tag } from '../components/tag';

export type GameAvailabilityStatus = 'active' | 'upcoming' | 'inactive';

/**
 * Availability of a game from `/games/visible`.
 *
 * Today this is always `'active'`: `/games/visible` already filters to
 * `isActive AND startDate <= now <= endDate`, so every option it returns is
 * live. The `'upcoming'` and `'inactive'` branches exist for a future
 * non-filtered source, and for the window between deploys when the API has
 * not started sending the three fields yet, where we assume active rather
 * than mislabel a live game.
 */
export function getGameAvailabilityStatus(
    game: {
        isActive?: boolean;
        startDate?: string;
        endDate?: string;
    },
    now: Date = new Date()
): GameAvailabilityStatus {
    if (game.isActive === false) {
        return 'inactive';
    }

    const startDate = game.startDate ? new Date(game.startDate) : null;
    const endDate = game.endDate ? new Date(game.endDate) : null;

    // An unparseable date tells us nothing; do not downgrade the tag on it.
    if (startDate && !isNaN(startDate.getTime()) && now < startDate) {
        return 'upcoming';
    }
    if (endDate && !isNaN(endDate.getTime()) && now > endDate) {
        return 'inactive';
    }
    return 'active';
}

export function GameAvailabilityStatusTag({
    status,
}: {
    status: GameAvailabilityStatus;
}) {
    switch (status) {
        case 'active':
            return <Tag variant="success">Active</Tag>;
        case 'upcoming':
            return <Tag variant="blue">Upcoming</Tag>;
        case 'inactive':
            return <Tag variant="grey">Not Active</Tag>;
    }
}
